import { makeAutoObservable } from 'mobx';

type ThemeName = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';

class ThemeStore {
  current: ThemeName = 'system';

  constructor() {
    makeAutoObservable(this);
  }

  /** Reads the persisted choice and applies it to the document. Call once on boot. */
  init() {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeName | null;
    this.current = stored ?? 'system';
    this.syncToDom();

    window.matchMedia?.('(prefers-color-scheme: dark)')?.addEventListener?.('change', () => {
      if (this.current === 'system') {
        this.syncToDom();
      }
    });
  }

  private apply(theme: 'light' | 'dark') {
    document.documentElement.setAttribute('data-theme', theme);
  }

  private systemPrefersDark(): boolean {
    return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
  }

  private syncToDom() {
    if (this.current === 'system') {
      this.apply(this.systemPrefersDark() ? 'dark' : 'light');
    } else {
      this.apply(this.current);
    }
  }

  setLight() {
    this.current = 'light';
    localStorage.setItem(STORAGE_KEY, this.current);
    this.syncToDom();
  }

  setDark() {
    this.current = 'dark';
    localStorage.setItem(STORAGE_KEY, this.current);
    this.syncToDom();
  }

  setSystem() {
    this.current = 'system';
    localStorage.setItem(STORAGE_KEY, this.current);
    this.syncToDom();
  }

  toggle() {
    if (this.isDark) {
      this.setLight();
    } else {
      this.setDark();
    }
  }

  get isDark(): boolean {
    if (this.current === 'system') {
      return this.systemPrefersDark();
    }
    return this.current === 'dark';
  }

  get value(): ThemeName {
    return this.current;
  }
}

const themeStore = new ThemeStore();

export default themeStore;
